import React from 'react';
import { useTechnicians } from '../../../hooks/useTechnicians';

/**
 * Quem executou o procedimento.
 *
 * Grava o id E o nome. O nome parece redundante tendo o id, mas é o que faz o
 * registro histórico continuar legível: técnico sai da equipe, é desativado, e
 * a sessão de três anos atrás ainda precisa dizer quem fez. Guardar só o id
 * deixaria o passado dependendo de uma linha que ninguém garante que fica.
 *
 * Sessões antigas têm nome digitado à mão e nenhum id. Esse nome entra na lista
 * como opção desabilitada em vez de sumir — apagar da tela um dado que está
 * gravado é pior que mostrá-lo sem poder reescolhê-lo.
 *
 * NOME SEM ID QUE BATE COM UM TÉCNICO É ESSE TÉCNICO. As sessões de OPU, FIV,
 * TE e DG não têm coluna para o id — gravam só o nome. Ao reabrir uma etapa
 * concluída, o id vinha nulo, o nome batia com alguém da lista (então não era
 * "registro antigo"), e o select caía no "Selecione": o técnico parecia ter
 * sumido, e salvar de novo exigia escolhê-lo outra vez.
 */
const TechnicianSelect = ({
  laboratoryId,
  technicianId,
  technicianName,
  onChange,
  label = 'Técnico',
  obrigatorio = true,
  // O ajudante não pode ser o próprio responsável.
  excluirId = null,
  // O que dizer quando vazio. `undefined` mantém o aviso de concluir a etapa.
  dica,
  className = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
}) => {
  const { technicians } = useTechnicians(laboratoryId);

  const peloNome = !technicianId && technicianName
    ? technicians.find(t => t.name === technicianName)
    : null;
  const idAtual = technicianId || peloNome?.id || null;

  const ativos = technicians.filter(t => (t.active || t.id === idAtual) && (!excluirId || t.id !== excluirId));
  const nomeSolto = technicianName && !idAtual;

  // Valor próprio para o nome antigo. Com `value=""` ele empataria com o
  // "Selecione" e o select mostraria o placeholder — o nome gravado sumiria da
  // tela mesmo estando no banco.
  const VALOR_LEGADO = '__registro_antigo__';
  const valorAtual = idAtual || (nomeSolto ? VALOR_LEGADO : '');

  const handleChange = (e) => {
    const id = e.target.value;
    if (id === VALOR_LEGADO) return;
    const escolhido = technicians.find(t => t.id === id);
    onChange(id || null, escolhido?.name || '');
  };

  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label}{obrigatorio && ' *'}
      </label>
      <select className={className} value={valorAtual} onChange={handleChange}>
        <option value="">Selecione</option>
        {ativos.map(t => (
          <option key={t.id} value={t.id}>
            {t.active ? t.name : `${t.name} (inativo)`}
          </option>
        ))}
        {nomeSolto && (
          <option value={VALOR_LEGADO}>{technicianName} (registro antigo)</option>
        )}
      </select>
      {technicians.length === 0 && (
        <p className="text-xs text-slate-400 mt-1">
          Nenhum técnico cadastrado — veja a aba Laboratório.
        </p>
      )}
      {technicians.length > 0 && !idAtual && !technicianName && (dica ?? (obrigatorio && 'Necessário para concluir a etapa.')) && (
        <p className="text-xs text-slate-500 mt-1">{dica ?? 'Necessário para concluir a etapa.'}</p>
      )}
    </div>
  );
};

export default TechnicianSelect;
